import { createListenerMiddleware } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '@/store/store'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { AgentV2ServerEventType } from '@/agent-v2/store/schemas'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { MedChronAssetActions } from '@/medical-chronology/asset/store/slice'
import { MedChronUpdateStatus } from '@/medical-chronology/asset/store/schemas'
import { MedSummaryAssetActions } from '@/medical-billing-summaries/asset/store/slice'
import { MedSummaryUpdateStatus } from '@/medical-billing-summaries/asset/store/schemas'
import { MedSummaryEntryEditActions } from '@/medical-billing-summaries/entries/store/entries-edit.slice'
import { TimelineAssetActions } from '@/timeline/asset/store/slice'
import { TimelineUpdateStatus } from '@/timeline/asset/store/schemas'
import { getMatterActivity } from '../fetch/get-matter-activity'
import { isDraftRow } from '../schemas'
import { MatterActivityFeedActions, kStaleActivityTimestamp } from './slice'

export const matterActivityFeedListenerMiddleware = createListenerMiddleware()
const startListener = matterActivityFeedListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

// How long a matter's feed stays fresh before a non-forced refresh refetches it.
const kMatterActivityCacheDurationMs = 60 * 1000

/**
 * Refresh Matter Activity Listener
 *
 * Loads the matter's most recent activity, replacing whatever was cached for it.
 */
startListener({
  actionCreator: MatterActivityFeedActions.refreshMatterActivity,
  effect: async (action, listenerApi) => {
    const { matterId, forceRefresh } = action.payload
    const entry = listenerApi.getState().matterActivityFeedState.byMatterId[matterId]

    if (entry?.isLoading) return
    const lastRefresh = entry?.lastRefresh ?? null
    const cacheExpired = lastRefresh === null || Date.now() - lastRefresh > kMatterActivityCacheDurationMs
    if (!cacheExpired && !forceRefresh) return

    try {
      listenerApi.dispatch(MatterActivityFeedActions.setActivityLoading({ matterId, loading: true }))
      listenerApi.dispatch(MatterActivityFeedActions.setActivityError({ matterId, error: false }))

      const response = await getMatterActivity(matterId)
      listenerApi.dispatch(MatterActivityFeedActions.setActivityRows({ matterId, rows: response.rows, hasMore: response.has_more }))
      // Cleared before the refetch below, which the in-flight guard would otherwise turn away.
      listenerApi.dispatch(MatterActivityFeedActions.setActivityLoading({ matterId, loading: false }))

      // If an invalidation landed while this fetch was in flight, the result may predate it, so refetch
      // rather than stamping the cache fresh (which would bury the invalidation until the next visit).
      // The `lastRefresh` guard prevents a loop: the forced refetch this triggers starts already-stale,
      // so its own completion takes the stamp-fresh branch instead of re-triggering.
      const invalidatedMidFetch =
        listenerApi.getState().matterActivityFeedState.byMatterId[matterId]?.lastRefresh === kStaleActivityTimestamp && lastRefresh !== kStaleActivityTimestamp
      if (invalidatedMidFetch) {
        listenerApi.dispatch(MatterActivityFeedActions.refreshMatterActivity({ matterId, forceRefresh: true }))
      } else {
        listenerApi.dispatch(MatterActivityFeedActions.setActivityLastRefresh({ matterId, timestamp: Date.now() }))
      }
    } catch {
      // Only a completed fetch advances the cache, so a failure stays retryable on the next visit
      // instead of reading as fresh. The rows already held stay put.
      listenerApi.dispatch(MatterActivityFeedActions.setActivityError({ matterId, error: true }))
      listenerApi.dispatch(MatterActivityFeedActions.setActivityLoading({ matterId, loading: false }))
    }
  },
})

/**
 * Invalidate Activity On Run Or Draft Completion
 *
 * A completed turn moves its chat's row, and a completed draft moves its draft's row.
 */
startListener({
  actionCreator: AgentV2TranscriptActions.serverEventReceived,
  effect: (action, listenerApi) => {
    const eventType = action.payload.event.type
    if (eventType !== AgentV2ServerEventType.RUN_COMPLETED && eventType !== AgentV2ServerEventType.DRAFT_COMPLETED) return

    const matterId = selectAgentV2ConversationMatterId(listenerApi.getState(), { conversationId: action.payload.conversationId })
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

/**
 * Invalidate Activity On Matter Artifact Version Advance
 *
 * Manual saves and accepted chat edits create a new immutable version, which moves the draft's
 * row. Ignore historical versions and repeated hydration of the current one.
 */
startListener({
  actionCreator: MatterArtifactsActions.upsertMatterArtifactVersion,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const previousCurrentVersion = Object.values(originalState.matterArtifactsState.versionsById).find(
      (version) => version?.matter_artifact_id === action.payload.matter_artifact_id && version.is_current
    )
    if (!action.payload.is_current || !previousCurrentVersion || previousCurrentVersion.id === action.payload.id) return

    const matterId = findMatterIdForArtifact(originalState, action.payload.matter_artifact_id)
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

/**
 * Invalidate Activity On Conversation Title Change
 *
 * A chat row carries its title, so renaming the chat changes what the row reads. A summary for a
 * chat the store has not seen yet is hydration rather than a change to one already on screen.
 */
startListener({
  actionCreator: AgentV2ConversationsActions.upsertConversationFromSummary,
  effect: (action, listenerApi) => {
    const previous = listenerApi.getOriginalState().agentV2ConversationsState.conversations[action.payload.id]
    if (!previous || previous.title === action.payload.title) return

    listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId: action.payload.matter_id }))
  },
})

/**
 * Invalidate Activity On Conversation Delete
 *
 * A deleted chat takes its own row and any draft rows from it out of the feed.
 */
startListener({
  actionCreator: AgentV2ConversationsActions.deleteConversation,
  effect: (action, listenerApi) => {
    const matterId = selectAgentV2ConversationMatterId(listenerApi.getOriginalState(), { conversationId: action.payload.conversationId })
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

/**
 * Invalidate Activity On Matter Record Update Completion
 *
 * A finished initialize or add-files run moves the record's row. Each record type polls its own
 * runs in its own slice, so each gets its own listener over the shared completion rule. Removals
 * are not runs: they invalidate from the dialog that performs them.
 */
startListener({
  actionCreator: MedChronAssetActions.setUpdateStatus,
  effect: (action, listenerApi) => {
    const matterId = matterIdForCompletedRun(action.payload, listenerApi.getOriginalState().medChronAssetState, MedChronUpdateStatus.DONE)
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

startListener({
  actionCreator: MedSummaryAssetActions.setUpdateStatus,
  effect: (action, listenerApi) => {
    const matterId = matterIdForCompletedRun(action.payload, listenerApi.getOriginalState().medSummaryAssetState, MedSummaryUpdateStatus.DONE)
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

startListener({
  actionCreator: TimelineAssetActions.setUpdateStatus,
  effect: (action, listenerApi) => {
    const matterId = matterIdForCompletedRun(action.payload, listenerApi.getOriginalState().timelineAssetState, TimelineUpdateStatus.DONE)
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

/**
 * Invalidate Activity On Billing Summary Cell Edit
 *
 * A saved cell edit counts as an update to the billing summary, and it reaches the record outside
 * the run the other record activity comes from.
 */
startListener({
  actionCreator: MedSummaryEntryEditActions.saveFieldSucceeded,
  effect: (action, listenerApi) => {
    const matterId = findMatterIdForRecordAsset(listenerApi.getOriginalState().medSummaryAssetState.assetByMatterId, action.payload.parentId)
    if (matterId) listenerApi.dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  },
})

/* Each record type reports these run and asset fields, under its own types. */
type RecordUpdateStatus = { update: { id: string; status: string } }
type RecordAsset = { id: string; matter_id: string }
type RecordAssetState = {
  updateStatusByAssetId: Record<string, RecordUpdateStatus | undefined>
  assetByMatterId: Record<string, RecordAsset | null | undefined>
}

/**
 * The matter whose row this run just moved, or null when the run has not reached its terminal
 * success or no cached asset matches it. A poll loop stamps its result repeatedly and consecutive
 * runs on the same record both end done, so the update id decides what counts as newly finished.
 */
function matterIdForCompletedRun(
  { assetId, status }: { assetId: string; status: RecordUpdateStatus },
  assetState: RecordAssetState,
  doneStatus: string
): string | null {
  if (status.update.status !== doneStatus) return null

  const previous = assetState.updateStatusByAssetId[assetId]
  const alreadyReported = previous?.update.id === status.update.id && previous.update.status === doneStatus
  if (alreadyReported) return null

  return findMatterIdForRecordAsset(assetState.assetByMatterId, assetId)
}

/** The matter holding this record asset, or null when no cached asset matches. */
function findMatterIdForRecordAsset(assetByMatterId: Record<string, RecordAsset | null | undefined>, assetId: string): string | null {
  for (const asset of Object.values(assetByMatterId)) {
    if (asset?.id === assetId) return asset.matter_id
  }
  return null
}

/** The matter whose cached rows include this draft, or null when no cached feed shows it. */
function findMatterIdForArtifact(state: RootState, matterArtifactId: string): string | null {
  for (const [matterId, entry] of Object.entries(state.matterActivityFeedState.byMatterId)) {
    const showsArtifact = entry?.rows.some((row) => isDraftRow(row) && row.artifact_id === matterArtifactId)
    if (showsArtifact) return matterId
  }
  return null
}
